/**
 * Settings Page
 *
 * Configuration management including export to TypeScript files.
 * Uses EITELKEIT tokens for styling.
 */

import { useState, type CSSProperties } from 'react';
import { tokens } from '../local-components';
import { useAgent, type ExportResponse } from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const sectionStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[5],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[4],
};

const sectionTitleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const sectionDescStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  lineHeight: 1.5,
};

const formGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[2],
};

const labelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inputStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
  outline: 'none',
};

const checkboxContainerStyle: CSSProperties = {
  display: 'flex',
  gap: tokens.spacing[4],
};

const checkboxLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
  cursor: 'pointer',
};

const checkboxStyle: CSSProperties = {
  width: '16px',
  height: '16px',
  accentColor: tokens.colors.mint,
  cursor: 'pointer',
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
  alignSelf: 'flex-start',
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[3],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const successStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.mint,
  padding: tokens.spacing[3],
  backgroundColor: 'rgba(0, 255, 135, 0.1)',
  border: `1px solid ${tokens.colors.mint}`,
  borderRadius: tokens.radii.md,
};

const fileListStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: tokens.spacing[3],
  maxHeight: '200px',
  overflowY: 'auto',
};

const fileItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
  padding: `${tokens.spacing[1]} 0`,
};

const fileTypeTagStyle = (type: string): CSSProperties => ({
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: type === 'artifact' ? tokens.colors.mint : tokens.colors.info,
  backgroundColor: type === 'artifact' ? 'rgba(0, 255, 135, 0.1)' : 'rgba(59, 130, 246, 0.1)',
  padding: `2px ${tokens.spacing[2]}`,
  borderRadius: tokens.radii.sm,
  textTransform: 'uppercase',
});

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: tokens.spacing[4],
};

const statCardStyle: CSSProperties = {
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.md,
  padding: tokens.spacing[4],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[1],
};

const statValueStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.mint,
};

const statLabelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Settings() {
  const { isConnected, exportConfig, listArtifacts, listEnvironments } = useAgent();
  const [outputDir, setOutputDir] = useState('.brutal');
  const [exportArtifacts, setExportArtifacts] = useState(true);
  const [exportEnvironments, setExportEnvironments] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stats
  const [artifactCount, setArtifactCount] = useState<number | null>(null);
  const [environmentCount, setEnvironmentCount] = useState<number | null>(null);

  // Fetch counts on mount
  useState(() => {
    if (isConnected) {
      listArtifacts()
        .then((arts) => setArtifactCount(arts.length))
        .catch(() => {});
      listEnvironments()
        .then((envs) => setEnvironmentCount(envs.length))
        .catch(() => {});
    }
  });

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    setExportResult(null);

    try {
      const types: ('artifacts' | 'environments')[] = [];
      if (exportArtifacts) types.push('artifacts');
      if (exportEnvironments) types.push('environments');

      if (types.length === 0) {
        setError('Select at least one type to export');
        setIsExporting(false);
        return;
      }

      const result = await exportConfig(outputDir, types);
      setExportResult(result);

      if (!result.success) {
        setError(result.error ?? 'Export failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export config');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Settings</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Settings</h1>
      </div>

      {/* Config Stats */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Configuration Overview</h2>
        <div style={gridStyle}>
          <div style={statCardStyle}>
            <span style={statValueStyle}>{artifactCount ?? '—'}</span>
            <span style={statLabelStyle}>Artifacts</span>
          </div>
          <div style={statCardStyle}>
            <span style={statValueStyle}>{environmentCount ?? '—'}</span>
            <span style={statLabelStyle}>Environments</span>
          </div>
        </div>
      </div>

      {/* Export Section */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Export Configuration</h2>
        <p style={sectionDescStyle}>
          Export your configuration from the database to TypeScript files for version control. The
          exported files will be placed in the specified output directory on the agent host.
        </p>

        {/* Output Directory */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Output Directory</label>
          <input
            type="text"
            value={outputDir}
            onChange={(e) => setOutputDir(e.target.value)}
            placeholder=".brutal"
            style={inputStyle}
          />
        </div>

        {/* Export Types */}
        <div style={formGroupStyle}>
          <label style={labelStyle}>Export Types</label>
          <div style={checkboxContainerStyle}>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={exportArtifacts}
                onChange={(e) => setExportArtifacts(e.target.checked)}
                style={checkboxStyle}
              />
              Artifacts
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={exportEnvironments}
                onChange={(e) => setExportEnvironments(e.target.checked)}
                style={checkboxStyle}
              />
              Environments
            </label>
          </div>
        </div>

        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={isExporting || !isConnected}
          style={isExporting || !isConnected ? buttonDisabledStyle : buttonStyle}
        >
          {isExporting ? 'Exporting...' : 'Export to TypeScript'}
        </button>

        {/* Error */}
        {error !== null && <div style={errorStyle}>Error: {error}</div>}

        {/* Success */}
        {exportResult !== null && exportResult.success && (
          <div style={successStyle}>Successfully exported {exportResult.files.length} file(s)</div>
        )}

        {/* File List */}
        {exportResult !== null && exportResult.files.length > 0 && (
          <div style={formGroupStyle}>
            <label style={labelStyle}>Exported Files</label>
            <div style={fileListStyle}>
              {exportResult.files.map((file, i) => (
                <div key={i} style={fileItemStyle}>
                  <span style={fileTypeTagStyle(file.type)}>{file.type}</span>
                  <span>{file.path}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Agent Info Section */}
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Agent Connection</h2>
        <p style={sectionDescStyle}>
          Connected to the BRUTAL agent. Configuration is stored in PostgreSQL and can be exported
          to TypeScript files for Git versioning.
        </p>
        <div style={gridStyle}>
          <div style={statCardStyle}>
            <span style={{ ...statValueStyle, color: tokens.colors.mint }}>●</span>
            <span style={statLabelStyle}>Status: Connected</span>
          </div>
        </div>
      </div>
    </div>
  );
}
